import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useNavigate,
  useOutletContext,
} from 'react-router-dom';

import {
  BarChart3,
  Bell,
  Building2,
  CheckCircle2,
  Clock3,
  CreditCard,
  FileText,
  LayoutDashboard,
  Loader2,
  LogOut,
  Menu,
  MessageSquare,
  RefreshCw,
  RotateCcw,
  Settings,
  ShieldCheck,
  Sparkles,
  Trash2,
  Users,
  Wallet,
  X,
  XCircle,
} from 'lucide-react';

import './ControlCenterVendors.css';
import './ControlCenterNotifications.css';


const ControlCenterNotifications = () => {
  const navigate = useNavigate();
  const outletContext = useOutletContext();

  let savedAdmin = null;

  try {
    const rawAdmin =
      localStorage.getItem('vakra_control_admin') ||
      sessionStorage.getItem('vakra_control_admin');

    if (rawAdmin) {
      savedAdmin = JSON.parse(rawAdmin);
    }
  } catch (error) {
    console.error('Saved admin parse error:', error);
  }

  const admin =
    outletContext?.admin ||
    savedAdmin ||
    null;

  const getToken = () =>
    localStorage.getItem('vakra_control_admin_token') ||
    sessionStorage.getItem('vakra_control_admin_token') ||
    '';

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [openPanel, setOpenPanel] = useState('');

  const [overview, setOverview] = useState({
    total: 0,
    pending: 0,
    scheduled: 0,
    processing: 0,
    sent: 0,
    delivered: 0,
    displayed: 0,
    clicked: 0,
    failed: 0,
    cancelled: 0,
    createdToday: 0,
    sentToday: 0,
  });

  const [queue, setQueue] = useState([]);
  const [logs, setLogs] = useState([]);

  const [settings, setSettings] = useState({
    quiet_hours_enabled: true,
    quiet_hours_start: '22:00',
    quiet_hours_end: '09:00',
    digest_cooldown_hours: 72,
    max_digest_notifications_7d: 3,
    max_attempts: 3,
  });

  const [savingSettings, setSavingSettings] = useState(false);
  const [actionId, setActionId] = useState(null);

  const [manualVendors, setManualVendors] = useState([]);
  const [manualCustomers, setManualCustomers] = useState([]);
  const [manualAnonymousDevices, setManualAnonymousDevices] = useState([]);
  const [manualAreas, setManualAreas] = useState([]);
  const [manualFoods, setManualFoods] = useState([]);
  const [manualSending, setManualSending] = useState(false);
  const [manualStatus, setManualStatus] = useState('');
  const [selectedVendorIds, setSelectedVendorIds] = useState([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [selectedAnonymousTokenIds, setSelectedAnonymousTokenIds] = useState([]);

  const [manualFilters, setManualFilters] = useState({
    vendorArea: '',
    customerArea: '',
    food: '',
    offerMode: 'all',
    originVendorId: '',
    visitedVendorId: '',
    orderedVendorId: '',
    minOrders: '1',
  });

  const [manualForm, setManualForm] = useState({
    title: '',
    body: '',
    deepLink: '',
  });

  const menuItems = [
    {
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/control-center/dashboard',
    },
    {
      label: 'Vendors',
      icon: Building2,
      path: '/control-center/vendors',
    },
    {
      label: 'Subscriptions',
      icon: CreditCard,
      path: '/control-center/subscriptions',
    },
    {
      label: 'Payments',
      icon: Wallet,
      path: '/control-center/payments',
    },
    {
      label: 'Agreements',
      icon: FileText,
      path: '/control-center/agreements',
    },
    {
      label: 'Smart Offer Digest',
      icon: Sparkles,
      path: '/control-center/smart-digest',
    },
    {
      label: 'Notifications',
      icon: Bell,
      path: '/control-center/notifications',
      active: true,
    },
    {
      label: 'Analytics',
      icon: BarChart3,
      path: '/control-center/analytics',
    },
    {
      label: 'Support',
      icon: MessageSquare,
      path: '/control-center/support',
    },
    {
      label: 'Settings',
      icon: Settings,
      path: '/control-center/settings',
    },
  ];


  const handleAuthFailure = () => {
    localStorage.removeItem('vakra_control_admin_token');
    localStorage.removeItem('vakra_control_admin');

    sessionStorage.removeItem('vakra_control_admin_token');
    sessionStorage.removeItem('vakra_control_admin');

    navigate(
      '/control-center/login',
      {
        replace: true,
      }
    );
  };


  const apiRequest = async (
    path,
    options = {}
  ) => {
    const token = getToken();

    if (!token) {
      handleAuthFailure();
      throw new Error('Admin login required.');
    }

    const response = await fetch(
      `${window.location.origin}/api/control-center/notifications${path}`,
      {
        ...options,
        headers: {
          ...(options.body
            ? { 'Content-Type': 'application/json' }
            : {}),
          Authorization: `Bearer ${token}`,
          ...(options.headers || {}),
        },
      }
    );

    const data =
      await response.json().catch(() => ({}));

    if (
      response.status === 401 ||
      response.status === 403
    ) {
      handleAuthFailure();
      throw new Error('Admin session expire ho gaya.');
    }

    if (
      !response.ok ||
      data?.success === false
    ) {
      throw new Error(
        data?.details ||
        data?.error ||
        'Request complete nahi hua.'
      );
    }

    return data;
  };


  const loadOverview = async () => {
    const data =
      await apiRequest('/overview');

    setOverview({
      total:
        Number(data?.overview?.total || 0),
      pending:
        Number(data?.overview?.pending || 0),
      scheduled:
        Number(data?.overview?.scheduled || 0),
      processing:
        Number(data?.overview?.processing || 0),
      sent:
        Number(data?.overview?.sent || 0),
      delivered:
        Number(data?.overview?.delivered || 0),
      displayed:
        Number(data?.overview?.displayed || 0),
      clicked:
        Number(data?.overview?.clicked || 0),
      failed:
        Number(data?.overview?.failed || 0),
      cancelled:
        Number(data?.overview?.cancelled || 0),
      createdToday:
        Number(data?.overview?.createdToday || 0),
      sentToday:
        Number(data?.overview?.sentToday || 0),
    });
  };


  const loadQueue = async () => {
    const path =
      activeFilter === 'all'
        ? '/queue?limit=200'
        : `/queue?status=${encodeURIComponent(
            activeFilter
          )}&limit=200`;

    const data =
      await apiRequest(path);

    setQueue(
      Array.isArray(data.notifications)
        ? data.notifications
        : []
    );
  };


  const loadLogs = async () => {
    const data =
      await apiRequest('/logs?limit=150');

    setLogs(
      Array.isArray(data.logs)
        ? data.logs
        : []
    );
  };


  const loadSettings = async () => {
    const data =
      await apiRequest('/settings');

    setSettings((prev) => ({
      ...prev,
      ...(data.settings || {}),
    }));
  };



  const loadManualOptions = async () => {
    const data = await apiRequest('/manual-smart-options');

    setManualVendors(
      Array.isArray(data.vendors) ? data.vendors : []
    );

    setManualCustomers(
      Array.isArray(data.customers) ? data.customers : []
    );

    setManualAnonymousDevices(
      Array.isArray(data.anonymousDevices) ? data.anonymousDevices : []
    );

    setManualAreas(
      Array.isArray(data.filters?.areas) ? data.filters.areas : []
    );

    setManualFoods(
      Array.isArray(data.filters?.foods) ? data.filters.foods : []
    );
  };


  const filteredManualVendors = useMemo(() => {
    const area = String(manualFilters.vendorArea || '')
      .trim()
      .toLowerCase();

    return manualVendors.filter((vendor) => {
      if (!area) return true;

      return String(vendor.area || '')
        .toLowerCase()
        .includes(area);
    });
  }, [manualVendors, manualFilters.vendorArea]);


  const filteredManualCustomers = useMemo(() => {
    const area = String(manualFilters.customerArea || '')
      .trim()
      .toLowerCase();

    const food = String(manualFilters.food || '')
      .trim()
      .toLowerCase();

    return manualCustomers.filter((customer) => {
      if (
        area &&
        !String(customer.mostOrderedArea || '')
          .toLowerCase()
          .includes(area)
      ) {
        return false;
      }

      if (
        food &&
        ![
          customer.favoriteFood,
          customer.favoriteCategory,
        ]
          .filter(Boolean)
          .some((value) =>
            String(value).toLowerCase().includes(food)
          )
      ) {
        return false;
      }

      if (
        manualFilters.offerMode === 'offer' &&
        Number(customer.activeOfferCount || 0) <= 0
      ) {
        return false;
      }

      if (
        manualFilters.offerMode === 'no_offer' &&
        Number(customer.activeOfferCount || 0) > 0
      ) {
        return false;
      }

      const originVendorId = Number(manualFilters.originVendorId || 0);
      if (
        originVendorId &&
        Number(customer.originVendorId || 0) !== originVendorId
      ) {
        return false;
      }

      const visitedVendorId = Number(manualFilters.visitedVendorId || 0);
      if (
        visitedVendorId &&
        !(Array.isArray(customer.visitedVendors) ? customer.visitedVendors : [])
          .some((visit) => Number(visit.vendorId || 0) === visitedVendorId)
      ) {
        return false;
      }

      const orderedVendorId = Number(manualFilters.orderedVendorId || 0);
      const minOrders = Math.max(1, Number(manualFilters.minOrders || 1));

      if (orderedVendorId) {
        const ordered = (Array.isArray(customer.orderedVendors)
          ? customer.orderedVendors
          : []
        ).find((item) => Number(item.vendorId || 0) === orderedVendorId);

        if (!ordered || Number(ordered.orderCount || 0) < minOrders) {
          return false;
        }
      }

      return true;
    });
  }, [manualCustomers, manualFilters]);


  const filteredManualAnonymousDevices = useMemo(() => {
    const originVendorId = Number(manualFilters.originVendorId || 0);
    const visitedVendorId = Number(manualFilters.visitedVendorId || 0);

    return manualAnonymousDevices.filter((device) => {
      if (
        originVendorId &&
        Number(device.originVendorId || 0) !== originVendorId
      ) {
        return false;
      }

      if (
        visitedVendorId &&
        !(Array.isArray(device.visitedVendors) ? device.visitedVendors : [])
          .some((visit) => Number(visit.vendorId || 0) === visitedVendorId)
      ) {
        return false;
      }

      return true;
    });
  }, [manualAnonymousDevices, manualFilters.originVendorId, manualFilters.visitedVendorId]);


  const getTrackingStage = (item = {}) => {
    if (item.clickedAt || Number(item.clickedDeviceCount || 0) > 0) {
      return 'clicked';
    }

    if (item.displayedAt || Number(item.displayedDeviceCount || 0) > 0) {
      return 'displayed';
    }

    if (item.deliveredAt || Number(item.deliveredDeviceCount || 0) > 0) {
      return 'delivered';
    }

    if (item.sentAt || Number(item.sentDeviceCount || 0) > 0) {
      return 'sent';
    }

    return item.status || 'pending';
  };


  const toggleVendorSelection = (vendorId) => {
    const id = Number(vendorId);

    setSelectedVendorIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );

    setManualStatus('');
  };


  const toggleCustomerSelection = (customerId) => {
    const id = Number(customerId);

    setSelectedCustomerIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );

    setManualStatus('');
  };


  const toggleAnonymousSelection = (tokenId) => {
    const id = Number(tokenId);
    setSelectedAnonymousTokenIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
    setManualStatus('');
  };


  const selectedManualVendors = useMemo(
    () =>
      manualVendors.filter((vendor) =>
        selectedVendorIds.includes(Number(vendor.id))
      ),
    [manualVendors, selectedVendorIds]
  );


  const selectedManualCustomers = useMemo(
    () =>
      manualCustomers.filter((customer) =>
        selectedCustomerIds.includes(Number(customer.id))
      ),
    [manualCustomers, selectedCustomerIds]
  );


  const selectedVendorOfferCount = useMemo(
    () =>
      selectedManualVendors.reduce(
        (sum, vendor) =>
          sum + (Number(vendor.activeOfferCount || 0) > 0 ? 1 : 0),
        0
      ),
    [selectedManualVendors]
  );


  const notificationFeaturedPreview = useMemo(() => {
    const firstOfferVendor =
      selectedManualVendors.find(
        (vendor) =>
          Number(vendor.activeOfferCount || 0) > 0 &&
          Array.isArray(vendor.offers) &&
          vendor.offers.length > 0
      ) ||
      selectedManualVendors[0] ||
      null;

    if (!firstOfferVendor) {
      return null;
    }

    const offer =
      Array.isArray(firstOfferVendor.offers)
        ? firstOfferVendor.offers[0] || null
        : null;

    return {
      vendorId: Number(firstOfferVendor.id || 0),
      vendorName: firstOfferVendor.name || 'Selected vendor',
      offerId: Number(offer?.id || 0) || null,
      offerName:
        offer?.offerName ||
        offer?.offerText ||
        'Food pick',
      price:
        Number(offer?.comboPrice || 0) ||
        Number(offer?.actualPrice || 0) ||
        0,
      imageUrl:
        offer?.imageUrl ||
        firstOfferVendor.imageUrl ||
        '',
    };
  }, [selectedManualVendors]);


  const sendManualNotification = async () => {
    if (!selectedVendorIds.length) {
      alert('Kam se kam ek vendor select karein.');
      return;
    }

    if (!selectedCustomerIds.length && !selectedAnonymousTokenIds.length) {
      alert('Kam se kam ek known customer ya anonymous QR device select karein.');
      return;
    }

    const recommendationCount = Math.max(
      0,
      selectedVendorIds.length - selectedVendorOfferCount
    );

    const confirmed = window.confirm(
      `${selectedCustomerIds.length + selectedAnonymousTokenIds.length} recipient(s) ko combined notification bhejna hai?\n` +
      `${selectedCustomerIds.length} known customer + ${selectedAnonymousTokenIds.length} anonymous device\n\n` +
      `${selectedVendorIds.length} vendors selected\n` +
      `${selectedVendorOfferCount} offer vendors\n` +
      `${recommendationCount} recommendation vendors`
    );

    if (!confirmed) return;

    try {
      setManualSending(true);
      setManualStatus('');

      const data = await apiRequest(
        '/send-manual-smart',
        {
          method: 'POST',
          body: JSON.stringify({
            vendorIds: selectedVendorIds,
            customerIds: selectedCustomerIds,
            anonymousTokenIds: selectedAnonymousTokenIds,
            title: String(manualForm.title || '').trim(),
            body: String(manualForm.body || '').trim(),
            deepLink: String(manualForm.deepLink || '').trim(),
          }),
        }
      );

      const result = data.result || {};

      setManualStatus(
        data.message ||
          `${Number(result.queued || 0)} combined notifications queue ho gaye.`
      );

      setSelectedCustomerIds([]);
      setSelectedAnonymousTokenIds([]);

      await Promise.all([
        loadOverview(),
        loadQueue(),
        loadLogs(),
        loadManualOptions(),
      ]);
    } catch (error) {
      setManualStatus(
        `Error: ${
          error.message ||
          'Manual Smart notification queue nahi hua.'
        }`
      );
    } finally {
      setManualSending(false);
    }
  };

  const loadAll = async () => {
    try {
      setLoading(true);
      setPageError('');

      await Promise.all([
        loadOverview(),
        loadQueue(),
        loadLogs(),
        loadSettings(),
        loadManualOptions(),
      ]);
    } catch (error) {
      console.error(
        'Notifications page load error:',
        error
      );

      setPageError(
        error.message ||
        'Notifications data load nahi hua.'
      );
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadAll();
  }, []);


  useEffect(() => {
    if (!loading) {
      loadQueue().catch((error) => {
        setPageError(error.message);
      });
    }
  }, [activeFilter]);


  const formatDateTime = (value) => {
    if (!value) return '-';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleString(
      'en-IN',
      {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }
    );
  };


  const formatTime12Hour = (value) => {
    if (!value) return '-';

    const [hourText, minute = '00'] =
      String(value).split(':');

    let hour = Number(hourText);

    if (Number.isNaN(hour)) {
      return value;
    }

    const period =
      hour >= 12
        ? 'PM'
        : 'AM';

    hour =
      hour % 12 || 12;

    return `${String(hour).padStart(2, '0')}:${minute} ${period}`;
  };


  const filteredCount = useMemo(
    () => queue.length,
    [queue]
  );


  const handleRetry = async (id) => {
    try {
      setActionId(id);

      await apiRequest(
        `/queue/${id}/retry`,
        {
          method: 'POST',
        }
      );

      await Promise.all([
        loadOverview(),
        loadQueue(),
        loadLogs(),
      ]);
    } catch (error) {
      alert(
        error.message ||
        'Retry nahi hua.'
      );
    } finally {
      setActionId(null);
    }
  };


  const handleCancel = async (id) => {
    const confirmed =
      window.confirm(
        'Is notification ko cancel karna hai?'
      );

    if (!confirmed) return;

    try {
      setActionId(id);

      await apiRequest(
        `/queue/${id}/cancel`,
        {
          method: 'POST',
        }
      );

      await Promise.all([
        loadOverview(),
        loadQueue(),
        loadLogs(),
      ]);
    } catch (error) {
      alert(
        error.message ||
        'Notification cancel nahi hua.'
      );
    } finally {
      setActionId(null);
    }
  };


  const saveSettings = async () => {
    try {
      setSavingSettings(true);

      const data =
        await apiRequest(
          '/settings',
          {
            method: 'PUT',
            body: JSON.stringify({
              settings: {
                quiet_hours_enabled:
                  Boolean(
                    settings.quiet_hours_enabled
                  ),

                quiet_hours_start:
                  settings.quiet_hours_start,

                quiet_hours_end:
                  settings.quiet_hours_end,

                digest_cooldown_hours:
                  Number(
                    settings.digest_cooldown_hours ||
                    0
                  ),

                max_digest_notifications_7d:
                  Number(
                    settings.max_digest_notifications_7d ||
                    0
                  ),

                max_attempts:
                  Number(
                    settings.max_attempts ||
                    0
                  ),
              },
            }),
          }
        );

      setSettings((prev) => ({
        ...prev,
        ...(data.settings || {}),
      }));

      alert(
        'Notification settings save ho gaye.'
      );
    } catch (error) {
      alert(
        error.message ||
        'Settings save nahi hue.'
      );
    } finally {
      setSavingSettings(false);
    }
  };


  const handleMenuClick = (item) => {
    setSidebarOpen(false);

    const enabledRoutes = [
      '/control-center/dashboard',
      '/control-center/vendors',
      '/control-center/subscriptions',
      '/control-center/payments',
      '/control-center/agreements',
      '/control-center/smart-digest',
      '/control-center/notifications',
    ];

    if (
      enabledRoutes.includes(
        item.path
      )
    ) {
      navigate(item.path);
      return;
    }

    alert(
      `${item.label} page next phase me banega.`
    );
  };


  const handleLogout = async () => {
    const token = getToken();

    try {
      if (token) {
        await fetch(
          `${window.location.origin}/api/control-center/logout`,
          {
            method: 'POST',
            headers: {
              Authorization:
                `Bearer ${token}`,
            },
          }
        );
      }
    } catch (error) {
      console.error(
        'Logout error:',
        error
      );
    }

    handleAuthFailure();
  };


  const filterButtons = [
    ['all', 'All'],
    ['pending', 'Pending'],
    ['scheduled', 'Scheduled'],
    ['processing', 'Processing'],
    ['sent', 'Sent'],
    ['delivered', 'Delivered'],
    ['failed', 'Failed'],
    ['cancelled', 'Cancelled'],
  ];


  return (
    <div className="vccv-app">

      {sidebarOpen && (
        <button
          type="button"
          className="vccv-overlay"
          onClick={() =>
            setSidebarOpen(false)
          }
          aria-label="Close menu"
        />
      )}


      <aside
        className={`vccv-sidebar ${
          sidebarOpen
            ? 'vccv-sidebar-open'
            : ''
        }`}
      >
        <div className="vccv-brand-row">
          <div className="vccv-brand">
            <div className="vccv-logo">
              V
            </div>

            <div>
              <strong>
                VAKRA
              </strong>

              <span>
                CONTROL CENTER
              </span>
            </div>
          </div>

          <button
            type="button"
            className="vccv-close"
            onClick={() =>
              setSidebarOpen(false)
            }
          >
            <X size={20} />
          </button>
        </div>


        <div className="vccv-admin-card">
          <div className="vccv-admin-avatar">
            {String(
              admin?.name || 'A'
            )
              .charAt(0)
              .toUpperCase()}
          </div>

          <div>
            <strong>
              {admin?.name ||
                'Administrator'}
            </strong>

            <span>
              {admin?.role ||
                'admin'}
            </span>
          </div>
        </div>


        <nav className="vccv-nav">
          {menuItems.map((item) => {
            const Icon = item.icon;

            return (
              <button
                key={item.label}
                type="button"
                className={`vccv-nav-item ${
                  item.active
                    ? 'active'
                    : ''
                }`}
                onClick={() =>
                  handleMenuClick(item)
                }
              >
                <Icon size={18} />

                <span>
                  {item.label}
                </span>

                {item.active && <i />}
              </button>
            );
          })}
        </nav>


        <div className="vccv-sidebar-bottom">
          <div className="vccv-secure">
            <ShieldCheck size={19} />

            <div>
              <strong>
                Secure Admin
              </strong>

              <span>
                Protected session
              </span>
            </div>
          </div>

          <button
            type="button"
            className="vccv-logout"
            onClick={handleLogout}
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </aside>


      <main className="vccv-main">
        <header className="vccv-topbar">
          <div className="vccv-top-left">
            <button
              type="button"
              className="vccv-mobile-menu"
              onClick={() =>
                setSidebarOpen(true)
              }
            >
              <Menu size={21} />
            </button>

            <div>
              <p>
                VAKRA CONTROL CENTER
              </p>

              <h1>
                Notifications
              </h1>
            </div>
          </div>


          <div className="vccv-top-right">
            <button
              type="button"
              className="vccv-notification"
              onClick={loadAll}
              title="Refresh"
            >
              <RefreshCw
                size={19}
                className={
                  loading
                    ? 'vccn-spin'
                    : ''
                }
              />
            </button>

            <div className="vccv-top-profile">
              <div className="vccv-profile-avatar">
                {String(
                  admin?.name || 'A'
                )
                  .charAt(0)
                  .toUpperCase()}
              </div>

              <div>
                <strong>
                  {admin?.name || 'Admin'}
                </strong>

                <span>
                  {admin?.email || ''}
                </span>
              </div>
            </div>
          </div>
        </header>


        <div className="vccv-content vccn-content">

          {pageError && (
            <div className="vccn-error">
              {pageError}
            </div>
          )}


          <section className="vccv-page-heading">
            <div>
              <span>
                <Bell size={16} />
                Delivery Control
              </span>

              <h2>
                Notification Center
              </h2>

              <p>
                Smart Digest queue, schedule,
                sent/failed tracking, retry,
                quiet hours aur cooldown yahan
                manage hoga.
              </p>
            </div>

            <button
              type="button"
              className="vccv-add-button"
              onClick={loadAll}
              disabled={loading}
            >
              <RefreshCw
                size={18}
                className={
                  loading
                    ? 'vccn-spin'
                    : ''
                }
              />

              {loading
                ? 'Loading...'
                : 'Refresh'}
            </button>
          </section>


          <section className="vccn-stats">
            <article>
              <span>Total Queue</span>
              <strong>{overview.total}</strong>
              <small>All notification jobs</small>
            </article>

            <article className="pending">
              <span>Pending</span>
              <strong>{overview.pending}</strong>
              <small>Waiting to send</small>
            </article>

            <article className="scheduled">
              <span>Scheduled</span>
              <strong>{overview.scheduled}</strong>
              <small>Future delivery</small>
            </article>

            <article className="sent">
              <span>Sent</span>
              <strong>{overview.sent}</strong>
              <small>Provider accepted</small>
            </article>

            <article className="delivered">
              <span>Delivered</span>
              <strong>{overview.delivered}</strong>
              <small>Device received</small>
            </article>

            <article className="delivered">
              <span>Displayed</span>
              <strong>{overview.displayed}</strong>
              <small>System popup shown</small>
            </article>

            <article className="delivered">
              <span>Clicked</span>
              <strong>{overview.clicked}</strong>
              <small>Customer opened</small>
            </article>

            <article className="failed">
              <span>Failed</span>
              <strong>{overview.failed}</strong>
              <small>Needs attention</small>
            </article>
          </section>


          <section
            className="vccn-panel"
            style={{
              marginBottom: 18,
              overflow: 'visible',
            }}
          >
            <div className="vccn-panel-heading">
              <div>
                <span>MANUAL SMART TARGETING</span>

                <h3>Multi Vendor + Multi Customer Notification</h3>

                <p>
                  Vendor location, customer ke most-ordered area, favourite food
                  aur offer availability ke basis par audience manually select karein.
                  Har selected customer ko ek combined notification jayega.
                </p>
              </div>

              <Users size={20} />
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
                gap: 12,
                padding: 18,
                borderBottom: '1px solid rgba(148,163,184,.18)',
              }}
            >
              <label style={{ display: 'grid', gap: 7 }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>
                  Vendor Location Filter
                </span>
                <select
                  value={manualFilters.vendorArea}
                  onChange={(event) =>
                    setManualFilters((prev) => ({
                      ...prev,
                      vendorArea: event.target.value,
                    }))
                  }
                  style={{ minHeight: 42, borderRadius: 10, padding: '0 10px' }}
                >
                  <option value="">All Vendor Areas</option>
                  {manualAreas.map((area) => (
                    <option key={`vendor-${area}`} value={area}>{area}</option>
                  ))}
                </select>
              </label>

              <label style={{ display: 'grid', gap: 7 }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>
                  Customer Most-Ordered Area
                </span>
                <select
                  value={manualFilters.customerArea}
                  onChange={(event) =>
                    setManualFilters((prev) => ({
                      ...prev,
                      customerArea: event.target.value,
                    }))
                  }
                  style={{ minHeight: 42, borderRadius: 10, padding: '0 10px' }}
                >
                  <option value="">All Customer Areas</option>
                  {manualAreas.map((area) => (
                    <option key={`customer-${area}`} value={area}>{area}</option>
                  ))}
                </select>
              </label>

              <label style={{ display: 'grid', gap: 7 }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>
                  Favourite Food
                </span>
                <select
                  value={manualFilters.food}
                  onChange={(event) =>
                    setManualFilters((prev) => ({
                      ...prev,
                      food: event.target.value,
                    }))
                  }
                  style={{ minHeight: 42, borderRadius: 10, padding: '0 10px' }}
                >
                  <option value="">All Foods</option>
                  {manualFoods.map((food) => (
                    <option key={food} value={food}>{food}</option>
                  ))}
                </select>
              </label>

              <label style={{ display: 'grid', gap: 7 }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>
                  Customer Offer Filter
                </span>
                <select
                  value={manualFilters.offerMode}
                  onChange={(event) =>
                    setManualFilters((prev) => ({
                      ...prev,
                      offerMode: event.target.value,
                    }))
                  }
                  style={{ minHeight: 42, borderRadius: 10, padding: '0 10px' }}
                >
                  <option value="all">Offer + No Offer</option>
                  <option value="offer">Offer Available</option>
                  <option value="no_offer">No Matching Offer</option>
                </select>
              </label>
              <label style={{ display: 'grid', gap: 7 }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>
                  First Notification Allow Vendor
                </span>
                <select
                  value={manualFilters.originVendorId}
                  onChange={(event) =>
                    setManualFilters((prev) => ({
                      ...prev,
                      originVendorId: event.target.value,
                    }))
                  }
                  style={{ minHeight: 42, borderRadius: 10, padding: '0 10px' }}
                >
                  <option value="">All Origin Vendors</option>
                  {manualVendors.map((vendor) => (
                    <option key={`origin-${vendor.id}`} value={vendor.id}>
                      {vendor.name} ({Number(vendor.acquiredCustomerCount || 0)})
                    </option>
                  ))}
                </select>
              </label>

              <label style={{ display: 'grid', gap: 7 }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>
                  QR Visited Vendor
                </span>
                <select
                  value={manualFilters.visitedVendorId}
                  onChange={(event) =>
                    setManualFilters((prev) => ({
                      ...prev,
                      visitedVendorId: event.target.value,
                    }))
                  }
                  style={{ minHeight: 42, borderRadius: 10, padding: '0 10px' }}
                >
                  <option value="">All Visited Vendors</option>
                  {manualVendors.map((vendor) => (
                    <option key={`visited-${vendor.id}`} value={vendor.id}>
                      {vendor.name} ({Number(vendor.visitedCustomerCount || 0)})
                    </option>
                  ))}
                </select>
              </label>

              <label style={{ display: 'grid', gap: 7 }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>
                  Ordered From Vendor
                </span>
                <select
                  value={manualFilters.orderedVendorId}
                  onChange={(event) =>
                    setManualFilters((prev) => ({
                      ...prev,
                      orderedVendorId: event.target.value,
                    }))
                  }
                  style={{ minHeight: 42, borderRadius: 10, padding: '0 10px' }}
                >
                  <option value="">All Ordered Vendors</option>
                  {manualVendors.map((vendor) => (
                    <option key={`ordered-${vendor.id}`} value={vendor.id}>
                      {vendor.name} ({Number(vendor.orderedCustomerCount || 0)})
                    </option>
                  ))}
                </select>
              </label>

              <label style={{ display: 'grid', gap: 7 }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>
                  Minimum Orders at Selected Vendor
                </span>
                <input
                  type="number"
                  min="1"
                  value={manualFilters.minOrders}
                  onChange={(event) =>
                    setManualFilters((prev) => ({
                      ...prev,
                      minOrders: event.target.value,
                    }))
                  }
                  disabled={!manualFilters.orderedVendorId}
                  style={{ minHeight: 42, borderRadius: 10, padding: '0 10px', border: '1px solid #cbd5e1' }}
                />
              </label>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: 16,
                padding: 18,
              }}
            >
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                  <div>
                    <strong>Select Vendors</strong>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                      {filteredManualVendors.length} vendors shown • {selectedVendorIds.length} selected
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedVendorIds(
                        filteredManualVendors.map((item) => Number(item.id))
                      )
                    }
                    style={{ border: 0, background: 'transparent', fontWeight: 700, cursor: 'pointer' }}
                  >
                    Select All
                  </button>
                </div>

                <div style={{ maxHeight: 360, overflow: 'auto', display: 'grid', gap: 8 }}>
                  {filteredManualVendors.map((vendor) => (
                    <label
                      key={vendor.id}
                      style={{
                        display: 'flex',
                        gap: 10,
                        alignItems: 'flex-start',
                        padding: 12,
                        border: selectedVendorIds.includes(Number(vendor.id))
                          ? '1px solid rgba(37,99,235,.45)'
                          : '1px solid rgba(148,163,184,.22)',
                        borderRadius: 12,
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedVendorIds.includes(Number(vendor.id))}
                        onChange={() => toggleVendorSelection(vendor.id)}
                      />
                      <div style={{ minWidth: 0 }}>
                        <strong>{vendor.name}</strong>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                          {vendor.area || 'Area unavailable'}
                        </div>
                        <div style={{ fontSize: 12, marginTop: 5, fontWeight: 700 }}>
                          {Number(vendor.activeOfferCount || 0) > 0
                            ? `✅ ${vendor.activeOfferCount} active offer(s)`
                            : '👍 No offer • Recommendation allowed'}
                        </div>
                        <div style={{ fontSize: 11, color: '#475569', marginTop: 5, lineHeight: 1.5 }}>
                          First Allow: {Number(vendor.acquiredCustomerCount || 0)} •{' '}
                          QR Visitors: {Number(vendor.visitedCustomerCount || 0)} •{' '}
                          Ordered: {Number(vendor.orderedCustomerCount || 0)} •{' '}
                          Repeat: {Number(vendor.repeatCustomerCount || 0)}
                        </div>
                      </div>
                    </label>
                  ))}

                  {!filteredManualVendors.length && (
                    <div style={{ padding: 14, color: '#64748b' }}>
                      Is filter me vendor nahi mila.
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                  <div>
                    <strong>Select Customers</strong>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                      {filteredManualCustomers.length} customers shown • {selectedCustomerIds.length} selected
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedCustomerIds(
                        filteredManualCustomers.map((item) => Number(item.id))
                      )
                    }
                    style={{ border: 0, background: 'transparent', fontWeight: 700, cursor: 'pointer' }}
                  >
                    Select All
                  </button>
                </div>

                <div style={{ maxHeight: 360, overflow: 'auto', display: 'grid', gap: 8 }}>
                  {filteredManualCustomers.map((customer) => (
                    <label
                      key={customer.id}
                      style={{
                        display: 'flex',
                        gap: 10,
                        alignItems: 'flex-start',
                        padding: 12,
                        border: selectedCustomerIds.includes(Number(customer.id))
                          ? '1px solid rgba(37,99,235,.45)'
                          : '1px solid rgba(148,163,184,.22)',
                        borderRadius: 12,
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCustomerIds.includes(Number(customer.id))}
                        onChange={() => toggleCustomerSelection(customer.id)}
                      />

                      <div style={{ minWidth: 0, flex: 1 }}>
                        <strong>
                          {customer.name || `Customer #${customer.id}`}
                        </strong>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                          {customer.mobile || 'Mobile unavailable'} • {customer.totalOrders || 0} orders
                        </div>
                        <div style={{ fontSize: 12, marginTop: 5 }}>
                          <b>Mostly eats from:</b> {customer.mostOrderedArea || '-'}
                        </div>
                        <div style={{ fontSize: 12, marginTop: 3 }}>
                          <b>Favourite food:</b> {customer.favoriteFood || customer.favoriteCategory || '-'}
                        </div>
                        <div style={{ fontSize: 12, marginTop: 3 }}>
                          <b>Favourite vendor:</b> {customer.favoriteVendorName || '-'}
                        </div>
                        <div style={{ fontSize: 12, marginTop: 3 }}>
                          <b>First notification allow:</b>{' '}
                          {customer.originVendorName || 'Not tracked yet'}
                        </div>
                        <div style={{ fontSize: 12, marginTop: 3 }}>
                          <b>QR visits:</b>{' '}
                          {Array.isArray(customer.visitedVendors) && customer.visitedVendors.length
                            ? customer.visitedVendors
                                .slice(0, 4)
                                .map((visit) => `${visit.vendorName} (${visit.scanCount})`)
                                .join(' • ')
                            : '-'}
                        </div>
                        <div style={{ fontSize: 12, marginTop: 3 }}>
                          <b>Orders by vendor:</b>{' '}
                          {Array.isArray(customer.orderedVendors) && customer.orderedVendors.length
                            ? customer.orderedVendors
                                .slice(0, 5)
                                .map((ordered) => `${ordered.vendorName} (${ordered.orderCount})`)
                                .join(' • ')
                            : '-'}
                        </div>
                        <div style={{ fontSize: 12, marginTop: 5, fontWeight: 700 }}>
                          {Number(customer.activeOfferCount || 0) > 0
                            ? `✅ ${customer.activeOfferCount} matching active offer(s)`
                            : '👍 No matching offer • Customer still selectable'}
                        </div>
                        {customer.bestOffer && (
                          <div style={{ fontSize: 12, marginTop: 4, color: '#15803d' }}>
                            Best offer: {customer.bestOffer.offerName} • {customer.bestOffer.vendorName}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}

                  {!filteredManualCustomers.length && (
                    <div style={{ padding: 14, color: '#64748b' }}>
                      Is filter me customer nahi mila.
                    </div>
                  )}
                </div>
              </div>

              <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                  <div>
                    <strong>Anonymous QR Devices</strong>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                      Notification Allow kiya hai, lekin customer profile se abhi link nahi hue.
                    </div>
                  </div>
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    {filteredManualAnonymousDevices.length} devices shown • {selectedAnonymousTokenIds.length} selected
                  </span>
                </div>

                <div style={{ maxHeight: 300, overflow: 'auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 8 }}>
                  {filteredManualAnonymousDevices.map((device) => (
                    <label
                      key={`anon-${device.id}`}
                      style={{
                        display: 'flex', gap: 10, alignItems: 'flex-start', padding: 12,
                        border: selectedAnonymousTokenIds.includes(Number(device.id))
                          ? '1px solid rgba(14,165,233,.55)'
                          : '1px solid rgba(148,163,184,.22)',
                        borderRadius: 12, cursor: 'pointer',
                        background: selectedAnonymousTokenIds.includes(Number(device.id))
                          ? 'rgba(14,165,233,.08)'
                          : 'transparent',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAnonymousTokenIds.includes(Number(device.id))}
                        onChange={() => toggleAnonymousSelection(device.id)}
                      />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <strong>Anonymous Device #{device.id}</strong>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, wordBreak: 'break-all' }}>
                          Device: {device.deviceKey || '-'}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
                          Platform: {device.platform || 'web'}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
                          Last seen: {formatDateTime(device.lastSeenAt)}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
                          Source: {device.source || 'food-radar'}
                        </div>
                        <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>
                          First notification allow: {device.originVendorName || 'Not tracked yet'}
                        </div>
                        <div style={{ fontSize: 11, color: '#475569', marginTop: 3, lineHeight: 1.5 }}>
                          QR visits: {Array.isArray(device.visitedVendors) && device.visitedVendors.length
                            ? device.visitedVendors
                                .slice(0, 4)
                                .map((visit) => `${visit.vendorName} (${visit.scanCount})`)
                                .join(' • ')
                            : '-'}
                        </div>
                      </div>
                    </label>
                  ))}

                  {!filteredManualAnonymousDevices.length && (
                    <div style={{ padding: 14, color: '#64748b' }}>
                      Koi active anonymous QR device nahi mila.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div
              style={{
                margin: '0 18px 18px',
                padding: 14,
                borderRadius: 12,
                background: 'rgba(37,99,235,.06)',
                border: '1px solid rgba(37,99,235,.15)',
              }}
            >
              <strong>Combined Notification Preview</strong>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 5 }}>
                {selectedVendorIds.length} vendors • {selectedVendorOfferCount} with offer •{' '}
                {Math.max(0, selectedVendorIds.length - selectedVendorOfferCount)} recommendations •{' '}
                {selectedCustomerIds.length} known customers • {selectedAnonymousTokenIds.length} anonymous devices
              </div>
              <div style={{ marginTop: 9, fontSize: 13 }}>
                <b>Auto Title:</b>{' '}
                {manualForm.title || `🔥 Aapke liye ${selectedVendorIds.length || 0} food picks`}
              </div>
              <div style={{ marginTop: 5, fontSize: 13 }}>
                <b>Auto Message:</b>{' '}
                {manualForm.body ||
                  `${selectedVendorOfferCount} special offers aur ${Math.max(
                    0,
                    selectedVendorIds.length - selectedVendorOfferCount
                  )} recommended vendors aapke liye ready hain. Tap karke sab dekhein.`}
              </div>
            </div>

            {notificationFeaturedPreview && (
              <div
                style={{
                  margin: '0 18px 18px',
                  display: 'grid',
                  gridTemplateColumns: notificationFeaturedPreview.imageUrl
                    ? '92px 1fr'
                    : '1fr',
                  gap: 12,
                  alignItems: 'center',
                  padding: 12,
                  borderRadius: 14,
                  background: '#fff7ed',
                  border: '1px solid #fed7aa',
                }}
              >
                {notificationFeaturedPreview.imageUrl ? (
                  <img
                    src={notificationFeaturedPreview.imageUrl}
                    alt={notificationFeaturedPreview.offerName}
                    style={{
                      width: 92,
                      height: 82,
                      objectFit: 'cover',
                      borderRadius: 12,
                    }}
                  />
                ) : null}

                <div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 900,
                      color: '#ea580c',
                      textTransform: 'uppercase',
                      letterSpacing: '.08em',
                    }}
                  >
                    Notification image preview
                  </div>

                  <div
                    style={{
                      marginTop: 4,
                      fontWeight: 900,
                      color: '#111827',
                    }}
                  >
                    {notificationFeaturedPreview.vendorName}
                  </div>

                  <div
                    style={{
                      marginTop: 2,
                      fontSize: 12,
                      color: '#475569',
                    }}
                  >
                    {notificationFeaturedPreview.offerName}
                    {notificationFeaturedPreview.price > 0
                      ? ` • ₹${notificationFeaturedPreview.price}`
                      : ''}
                  </div>

                  <div
                    style={{
                      marginTop: 5,
                      fontSize: 10,
                      color: '#64748b',
                    }}
                  >
                    Final image/customer pick backend automatically personalize karega.
                  </div>
                </div>
              </div>
            )}

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: 12,
                padding: '0 18px 18px',
              }}
            >
              <label style={{ display: 'grid', gap: 7 }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>
                  Notification Title (optional)
                </span>
                <input
                  type="text"
                  maxLength={220}
                  value={manualForm.title}
                  onChange={(event) =>
                    setManualForm((prev) => ({ ...prev, title: event.target.value }))
                  }
                  placeholder="Blank rakho to auto title banega"
                  style={{ minHeight: 44, borderRadius: 10, padding: '0 12px' }}
                />
              </label>

              <label style={{ display: 'grid', gap: 7 }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>
                  Click Open Link (optional)
                </span>
                <input
                  type="text"
                  value={manualForm.deepLink}
                  onChange={(event) =>
                    setManualForm((prev) => ({ ...prev, deepLink: event.target.value }))
                  }
                  placeholder="Blank = Food Radar offers page"
                  style={{ minHeight: 44, borderRadius: 10, padding: '0 12px' }}
                />
              </label>

              <label style={{ display: 'grid', gap: 7, gridColumn: '1 / -1' }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>
                  Message Box (optional)
                </span>
                <textarea
                  maxLength={500}
                  rows={4}
                  value={manualForm.body}
                  onChange={(event) =>
                    setManualForm((prev) => ({ ...prev, body: event.target.value }))
                  }
                  placeholder="Blank rakho to 2 offers + 3 recommendations jaisa combined message automatically banega."
                  style={{ borderRadius: 10, padding: 12, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </label>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
                padding: '0 18px 18px',
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  color: manualStatus.startsWith('Error:') ? '#dc2626' : '#15803d',
                  fontWeight: 700,
                }}
              >
                {manualStatus ||
                  `${manualCustomers.length} known customers • ${manualAnonymousDevices.length} anonymous devices • ${manualVendors.length} vendors available`}
              </div>

              <button
                type="button"
                className="vccv-add-button"
                onClick={sendManualNotification}
                disabled={
                  manualSending ||
                  !selectedVendorIds.length ||
                  (!selectedCustomerIds.length && !selectedAnonymousTokenIds.length)
                }
              >
                {manualSending ? (
                  <>
                    <Loader2 size={18} className="vccn-spin" />
                    Queueing...
                  </>
                ) : (
                  <>
                    <Bell size={18} />
                    Send Combined Notification
                  </>
                )}
              </button>
            </div>
          </section>

          <section className="vccn-panel">
            <div className="vccn-panel-heading">
              <div>
                <span>
                  NOTIFICATION QUEUE
                </span>

                <h3>
                  Customer Push Queue
                </h3>

                <p>
                  {filteredCount} records shown
                </p>
              </div>

              <Bell size={20} />
            </div>


            <div className="vccn-filters">
              {filterButtons.map(
                ([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className={
                      activeFilter === key
                        ? 'active'
                        : ''
                    }
                    onClick={() =>
                      setActiveFilter(key)
                    }
                  >
                    {label}
                  </button>
                )
              )}
            </div>


            <div className="vccn-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Customer</th>
                    <th>Campaign</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>Device Delivery</th>
                    <th>Schedule</th>
                    <th>Attempts</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {queue.length === 0 ? (
                    <tr>
                      <td
                        colSpan="9"
                        className="vccn-empty"
                      >
                        Queue me abhi koi
                        notification nahi hai.
                      </td>
                    </tr>
                  ) : (
                    queue.map((item) => (
                      <tr key={item.id}>
                        <td>
                          #{item.id}
                        </td>

                        <td>
                          <strong>
                            {item.customerName ||
                              `Customer #${item.customerId || '-'}`}
                          </strong>

                          <span>
                            {item.customerMobile ||
                              'Mobile unavailable'}
                          </span>
                        </td>

                        <td>
                          <strong>
                            {item.campaignName ||
                              (item.notificationType === 'manual_smart'
                                ? 'Manual Smart Targeting'
                                : `Digest #${item.digestRunId || '-'}`)}
                          </strong>

                          <span>
                            {item.notificationType}
                          </span>
                        </td>

                        <td className="vccn-message-cell">
                          <strong>
                            {item.title}
                          </strong>

                          <span>
                            {item.body}
                          </span>
                        </td>

                        <td>
                          <span
                            className={`vccn-status ${item.status}`}
                          >
                            {item.status}
                          </span>
                          <span style={{ marginTop: 5, fontSize: 11, textTransform: 'capitalize' }}>
                            Tracking: {getTrackingStage(item)}
                          </span>
                        </td>

                        <td style={{ minWidth: 220 }}>
                          <strong style={{ display: 'block', fontSize: 12 }}>
                            {Number(item.sentDeviceCount || 0)}/{Number(item.deviceCount || 0)} sent •{' '}
                            {Number(item.deliveredDeviceCount || 0)} delivered •{' '}
                            {Number(item.displayedDeviceCount || 0)} displayed •{' '}
                            {Number(item.clickedDeviceCount || 0)} clicked
                          </strong>

                          <strong
                            style={{
                              display: 'block',
                              fontSize: 12,
                              marginTop: 5,
                              color: '#0f766e',
                            }}
                          >
                            {Number(item.menuOpenedCount || 0)} menu opened •{' '}
                            {Number(item.orderPlacedCount || 0)} order placed
                          </strong>

                          {Array.isArray(item.attributedOrders) &&
                            item.attributedOrders.length > 0 && (
                              <div
                                style={{
                                  marginTop: 5,
                                  display: 'grid',
                                  gap: 2,
                                }}
                              >
                                {item.attributedOrders
                                  .slice(0, 3)
                                  .map((order) => (
                                    <span
                                      key={`${item.id}-order-${order.orderId}`}
                                      style={{
                                        fontSize: 10,
                                        color: '#0f766e',
                                      }}
                                    >
                                      Order #{order.orderId || '-'}
                                      {order.vendorId
                                        ? ` • Vendor #${order.vendorId}`
                                        : ''}
                                      {Number(order.orderAmount || 0) > 0
                                        ? ` • ₹${Number(order.orderAmount).toLocaleString('en-IN')}`
                                        : ''}
                                    </span>
                                  ))}
                              </div>
                            )}
                          {Number(item.failedDeviceCount || 0) > 0 && (
                            <span style={{ display: 'block', fontSize: 11, color: '#b91c1c', marginTop: 3 }}>
                              {item.failedDeviceCount} device failed
                            </span>
                          )}
                          {Array.isArray(item.devices) && item.devices.length > 0 && (
                            <div style={{ marginTop: 6, display: 'grid', gap: 3 }}>
                              {item.devices.slice(0, 4).map((device) => (
                                <span key={`${item.id}-${device.id}`} style={{ fontSize: 10, color: '#64748b' }}>
                                  {device.deviceKey || `Device ${device.tokenId || ''}`} → {device.status}
                                  {device.tokenActive === false ? ' • token inactive' : ''}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>

                        <td>
                          {item.scheduledAt
                            ? formatDateTime(
                                item.scheduledAt
                              )
                            : formatDateTime(
                                item.availableAt ||
                                item.createdAt
                              )}
                        </td>

                        <td>
                          {item.attemptCount}/
                          {item.maxAttempts}
                        </td>

                        <td>
                          <div className="vccn-actions">
                            {item.status ===
                              'failed' && (
                              <button
                                type="button"
                                className="retry"
                                disabled={
                                  actionId ===
                                  item.id
                                }
                                onClick={() =>
                                  handleRetry(
                                    item.id
                                  )
                                }
                                title="Retry"
                              >
                                {actionId ===
                                item.id ? (
                                  <Loader2
                                    size={15}
                                    className="vccn-spin"
                                  />
                                ) : (
                                  <RotateCcw
                                    size={15}
                                  />
                                )}
                              </button>
                            )}

                            {[
                              'pending',
                              'scheduled',
                            ].includes(
                              item.status
                            ) && (
                              <button
                                type="button"
                                className="cancel"
                                disabled={
                                  actionId ===
                                  item.id
                                }
                                onClick={() =>
                                  handleCancel(
                                    item.id
                                  )
                                }
                                title="Cancel"
                              >
                                <Trash2
                                  size={15}
                                />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>


          <section className="vccn-collapse-panel">
            <button
              type="button"
              className="vccn-collapse-toggle"
              onClick={() =>
                setOpenPanel((current) =>
                  current === 'settings'
                    ? ''
                    : 'settings'
                )
              }
            >
              <div>
                <span>
                  DELIVERY RULES
                </span>

                <h3>
                  Quiet Hours & Cooldown
                </h3>

                <p>
                  {settings.quiet_hours_enabled
                    ? `Quiet ${formatTime12Hour(
                        settings.quiet_hours_start
                      )} - ${formatTime12Hour(
                        settings.quiet_hours_end
                      )}`
                    : 'Quiet hours OFF'}
                  {' • '}
                  {settings.digest_cooldown_hours}h cooldown
                  {' • '}
                  Max {settings.max_digest_notifications_7d}/7 days
                </p>
              </div>

              <Settings size={20} />
            </button>


            {openPanel ===
              'settings' && (
              <div className="vccn-settings-grid">

                <label className="vccn-setting-toggle">
                  <span>
                    Quiet Hours Enabled
                  </span>

                  <input
                    type="checkbox"
                    checked={
                      Boolean(
                        settings.quiet_hours_enabled
                      )
                    }
                    onChange={(event) =>
                      setSettings(
                        (prev) => ({
                          ...prev,
                          quiet_hours_enabled:
                            event.target.checked,
                        })
                      )
                    }
                  />
                </label>


                <label>
                  <span>
                    Quiet Start
                  </span>

                  <input
                    type="time"
                    value={
                      settings.quiet_hours_start ||
                      '22:00'
                    }
                    onChange={(event) =>
                      setSettings(
                        (prev) => ({
                          ...prev,
                          quiet_hours_start:
                            event.target.value,
                        })
                      )
                    }
                  />
                </label>


                <label>
                  <span>
                    Quiet End
                  </span>

                  <input
                    type="time"
                    value={
                      settings.quiet_hours_end ||
                      '09:00'
                    }
                    onChange={(event) =>
                      setSettings(
                        (prev) => ({
                          ...prev,
                          quiet_hours_end:
                            event.target.value,
                        })
                      )
                    }
                  />
                </label>


                <label>
                  <span>
                    Digest Cooldown Hours
                  </span>

                  <input
                    type="number"
                    min="0"
                    value={
                      settings.digest_cooldown_hours
                    }
                    onChange={(event) =>
                      setSettings(
                        (prev) => ({
                          ...prev,
                          digest_cooldown_hours:
                            event.target.value,
                        })
                      )
                    }
                  />
                </label>


                <label>
                  <span>
                    Max Digest / 7 Days
                  </span>

                  <input
                    type="number"
                    min="1"
                    value={
                      settings.max_digest_notifications_7d
                    }
                    onChange={(event) =>
                      setSettings(
                        (prev) => ({
                          ...prev,
                          max_digest_notifications_7d:
                            event.target.value,
                        })
                      )
                    }
                  />
                </label>


                <label>
                  <span>
                    Max Retry Attempts
                  </span>

                  <input
                    type="number"
                    min="1"
                    value={
                      settings.max_attempts
                    }
                    onChange={(event) =>
                      setSettings(
                        (prev) => ({
                          ...prev,
                          max_attempts:
                            event.target.value,
                        })
                      )
                    }
                  />
                </label>


                <button
                  type="button"
                  className="vccn-save-settings"
                  onClick={saveSettings}
                  disabled={savingSettings}
                >
                  {savingSettings ? (
                    <>
                      <Loader2
                        size={16}
                        className="vccn-spin"
                      />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2
                        size={16}
                      />
                      Save Rules
                    </>
                  )}
                </button>
              </div>
            )}
          </section>


          <section className="vccn-collapse-panel">
            <button
              type="button"
              className="vccn-collapse-toggle"
              onClick={() =>
                setOpenPanel((current) =>
                  current === 'logs'
                    ? ''
                    : 'logs'
                )
              }
            >
              <div>
                <span>
                  HISTORY
                </span>

                <h3>
                  Notification Logs
                </h3>

                <p>
                  Queue, retry, cancel aur
                  send-status history.
                </p>
              </div>

              <Clock3 size={20} />
            </button>


            {openPanel === 'logs' && (
              <div className="vccn-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Queue</th>
                      <th>Customer</th>
                      <th>Campaign</th>
                      <th>Event</th>
                      <th>Status</th>
                      <th>Error</th>
                    </tr>
                  </thead>

                  <tbody>
                    {logs.length === 0 ? (
                      <tr>
                        <td
                          colSpan="7"
                          className="vccn-empty"
                        >
                          Logs available nahi hain.
                        </td>
                      </tr>
                    ) : (
                      logs.map((log) => (
                        <tr key={log.id}>
                          <td>
                            {formatDateTime(
                              log.createdAt
                            )}
                          </td>

                          <td>
                            #{log.queueId || '-'}
                          </td>

                          <td>
                            <strong>
                              {log.customerName ||
                                `Customer #${log.customerId || '-'}`}
                            </strong>

                            <span>
                              {log.customerMobile ||
                                '-'}
                            </span>
                          </td>

                          <td>
                            {log.campaignName ||
                              (log.notificationType === 'manual_smart'
                                ? 'Manual Smart Targeting'
                                : `Digest #${log.digestRunId || '-'}`)}
                          </td>

                          <td>
                            {log.eventType}
                          </td>

                          <td>
                            {log.oldStatus || '-'}
                            {' → '}
                            {log.newStatus || '-'}
                          </td>

                          <td>
                            {log.errorMessage ||
                              '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>


          <section className="vccn-info">
            <div>
              <CheckCircle2 size={18} />

              <p>
                Smart Digest ka
                <strong>
                  {' '}Send to Notification Queue{' '}
                </strong>
                button ab isi queue ko fill karega.
              </p>
            </div>

            <div>
              <XCircle size={18} />

              <p>
                Customer FCM registration active hai.
                Manual vendor-wise push aur Smart Digest
                dono isi notification center se send honge.
              </p>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
};


export default ControlCenterNotifications;
